import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { temaDe } from './temasJuegos';
import {
  JUEGOS_SIM, juegoDe, jugadaAlAzar, alAzarEntre, suenoDe,
} from './mecanicasJuegos';
import BotonCompartir from './Compartir';
import { imagenJugada } from './imagenCompartir';
import './Simulador.css';

const PUB = process.env.PUBLIC_URL;

/* Qué es esta pieza, y qué NO es.
   Es un **simulador educativo**: se arma el cartón para entender cómo se
   juega. No es una apuesta, no hay compra, no se guarda nada, no se pide un
   dato personal y no se avisa ningún resultado. INPROJUY no vende online, y
   la pieza no promete nada que el organismo no pueda cumplir.

   La mecánica de cada juego —cuántos números, de qué rango, qué modalidades—
   sale entera de `mecanicasJuegos.js`, con la fuente citada juego por juego.
   Acá no se decide ninguna regla de juego: este archivo solo la dibuja. */

const pad2 = (n) => String(n).padStart(2, '0');

/* ────────────────────── El texto que acompaña a la imagen ──────────────────
   Es el respaldo, no el entregable: lo que se comparte es una imagen, porque
   un texto se edita antes de reenviarlo. Igual arranca con lo mismo que la
   banda dorada de la imagen —"jugada simulada, no es una apuesta"— porque en
   WhatsApp la primera línea es lo único que se ve sin abrir el mensaje.

   ⚠️ Ni acá ni en la imagen se nombra un premio, un monto ni una
   participación en ningún sorteo. INPROJUY no vende online y el simulador es
   educativo: una jugada simulada reenviada tres veces no puede leerse como
   una apuesta hecha. */
function textoJugada({ juego, lineas, sueno }) {
  const partes = ['Jugada simulada. No es una apuesta.', '', juego, ...lineas];
  if (sueno) partes.push(sueno);
  partes.push(
    '',
    'La armé en el simulador de INPROJUY, que sirve para practicar cómo se juega.',
    'No es un ticket, no participa de ningún sorteo y no paga premio. Para jugar de verdad hay que ir a una agencia oficial.',
    '',
    'Armá la tuya:',
  );
  return partes.join('\n');
}

/* ───────────────────────── Una celda de la grilla ─────────────────────────
   El target va a 48×48 con 8 px de separación real (P6). El estado elegido
   NO se comunica solo por color: además del relleno lleva un anillo de tinta
   y el número en negrita, para que se lea sin distinguir matices.          */
function Celda({ etiqueta, elegido, onClick, aria, ancha }) {
  return (
    <button
      type="button"
      className={`sim-celda${elegido ? ' elegida' : ''}${ancha ? ' ancha' : ''}`}
      aria-pressed={elegido}
      aria-label={aria}
      onClick={onClick}
    >
      {etiqueta}
    </button>
  );
}

/* ─────────────────────────── Una bolilla del talón ───────────────────────── */
function Bolilla({ valor, extra, vacia }) {
  if (vacia) return <span className="sim-bolilla vacia" aria-hidden="true" />;
  return <span className={`sim-bolilla${extra ? ' extra' : ''}`}>{valor}</span>;
}

/* ═══════════════════════ Molde A · bolillero ═══════════════════════════ */
function CartonBolillero({ juego, elegidos, extra, onToggle, onExtra }) {
  const cuantos = juego.cantidad;
  const numeros = useMemo(() => {
    const a = [];
    for (let v = juego.min; v <= juego.max; v += 1) a.push(v);
    return a;
  }, [juego.min, juego.max]);

  const lleno = elegidos.length >= cuantos;

  return (
    <>
      <div className="sim-bloque">
        <div className="sim-bloque-head">
          <h4 className="sim-bloque-tit">
            Marcá tus {cuantos} números
          </h4>
          <span className="sim-contador" aria-hidden="true">
            {elegidos.length} de {cuantos}
          </span>
        </div>

        <div className="sim-grilla">
          {numeros.map((v) => (
            <Celda
              key={v}
              etiqueta={pad2(v)}
              elegido={elegidos.includes(v)}
              aria={`Número ${pad2(v)}`}
              onClick={() => onToggle(v)}
            />
          ))}
        </div>
        {lleno && (
          <p className="sim-pista">
            Ya elegiste los {cuantos}. Tocá uno de los marcados para cambiarlo.
          </p>
        )}
      </div>

      {juego.extra && (
        <div className="sim-bloque sim-bloque-extra">
          <div className="sim-bloque-head">
            <h4 className="sim-bloque-tit">
              Y marcá el {juego.extra.nombre}
            </h4>
          </div>
          <p className="sim-pista">{juego.extra.nota}</p>
          <div className="sim-grilla sim-grilla-extra">
            {Array.from(
              { length: juego.extra.max - juego.extra.min + 1 },
              (_, i) => juego.extra.min + i,
            ).map((v) => (
              <Celda
                key={v}
                etiqueta={v}
                elegido={extra === v}
                aria={`${juego.extra.nombre} ${v}`}
                onClick={() => onExtra(v)}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
}

/* ═══════════════════════ Molde B · quiniela ════════════════════════════ */
function CartonQuiniela({
  juego, cifras, numero, posicion, onCifras, onNumero, onPosicion, onVerSuenos,
}) {
  const dosUltimas = numero.length >= 2 ? numero.slice(-2) : null;
  const sueno = dosUltimas ? suenoDe(dosUltimas) : null;

  return (
    <>
      <div className="sim-bloque">
        <div className="sim-bloque-head">
          <h4 className="sim-bloque-tit">¿A cuántas cifras jugás?</h4>
        </div>
        <div className="sim-grilla sim-grilla-cifras">
          {[1, 2, 3, 4].map((c) => (
            <Celda
              key={c}
              etiqueta={c}
              elegido={cifras === c}
              aria={`${c} ${c === 1 ? 'cifra' : 'cifras'}`}
              onClick={() => onCifras(c)}
              ancha
            />
          ))}
        </div>
      </div>

      <div className="sim-bloque">
        <div className="sim-bloque-head">
          <label className="sim-bloque-tit" htmlFor="sim-numero">Tu número</label>
        </div>
        <input
          id="sim-numero"
          className="sim-numero"
          type="text"
          inputMode="numeric"
          autoComplete="off"
          maxLength={cifras}
          value={numero}
          placeholder={'—'.repeat(cifras)}
          aria-describedby="sim-numero-ayuda"
          onChange={(e) => onNumero(e.target.value.replace(/\D/g, '').slice(0, cifras))}
        />
        <p className="sim-pista" id="sim-numero-ayuda">
          {cifras === 4
            ? 'De 0000 a 9999.'
            : `Las últimas ${cifras} ${cifras === 1 ? 'cifra' : 'cifras'} del número sorteado.`}
        </p>

        {sueno && (
          <p className="sim-sueno">
            El <b>{dosUltimas}</b> es <b>{sueno}</b> en la tabla de los sueños.{' '}
            <button type="button" className="sim-link" onClick={onVerSuenos}>
              Ver la tabla
            </button>
          </p>
        )}
      </div>

      <div className="sim-bloque">
        <div className="sim-bloque-head">
          <h4 className="sim-bloque-tit">¿En qué posición?</h4>
          <span className="sim-contador" aria-hidden="true">
            {posicion === 1 ? 'la cabeza' : `${posicion}ª`}
          </span>
        </div>
        <p className="sim-pista">
          El extracto de cada turno tiene 20 números. La 1ª posición es <b>la cabeza</b>.
        </p>
        <div className="sim-grilla">
          {Array.from({ length: juego.posiciones }, (_, i) => i + 1).map((p) => (
            <Celda
              key={p}
              etiqueta={p}
              elegido={posicion === p}
              aria={p === 1 ? 'Posición 1, la cabeza' : `Posición ${p}`}
              onClick={() => onPosicion(p)}
            />
          ))}
        </div>
      </div>

    </>
  );
}

/* ═════════════════════════════ El simulador ════════════════════════════ */
export default function SimuladorJugada({
  clave, onClave, semilla, onVerSuenos,
}) {
  const juego = juegoDe(clave);
  const tema = temaDe(juego.clave, PUB);

  const [elegidos, setElegidos] = useState([]);
  const [extra, setExtra] = useState(null);
  const [cifras, setCifras] = useState(2);
  const [numero, setNumero] = useState('');
  const [posicion, setPosicion] = useState(1);

  // Al cambiar de juego el cartón arranca en blanco: una jugada de Loto Plus
  // no es una jugada de Brinco aunque los números entren en los dos rangos.
  useEffect(() => {
    setElegidos([]);
    setExtra(null);
    setNumero('');
    setCifras(2);
    setPosicion(1);
  }, [juego.clave]);

  // La semilla llega desde la tabla de los sueños ("jugar este número").
  // El efecto depende del dato, no del montaje: si llegara después —o dos
  // veces seguidas— igual se aplica. Es la trampa nº 1 del repo, al revés.
  useEffect(() => {
    if (!semilla) return;
    setCifras(semilla.cifras);
    setNumero(semilla.texto);
  }, [semilla]);

  const toggle = useCallback((v) => {
    setElegidos((prev) => {
      if (prev.includes(v)) return prev.filter((x) => x !== v);
      if (prev.length >= juego.cantidad) return prev;
      return [...prev, v].sort((a, b) => a - b);
    });
  }, [juego.cantidad]);

  const borrar = () => {
    setElegidos([]); setExtra(null); setNumero('');
  };

  // Después de llenarla, el talón se trae a la vista: la jugada armada es la
  // recompensa de la pieza y en un teléfono queda debajo del corte.
  const talonRef = useRef(null);
  const llenar = () => {
    if (juego.familia === 'bolillero') {
      setElegidos(jugadaAlAzar(juego));
      if (juego.extra) setExtra(alAzarEntre(juego.extra.min, juego.extra.max));
    } else {
      setNumero(String(alAzarEntre(0, 10 ** cifras - 1)).padStart(cifras, '0'));
      setPosicion(alAzarEntre(1, juego.posiciones));
    }
    const suave = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.requestAnimationFrame(() => {
      if (talonRef.current) {
        talonRef.current.scrollIntoView({
          block: 'nearest',
          behavior: suave ? 'smooth' : 'auto',
        });
      }
    });
  };

  const esBolillero = juego.familia === 'bolillero';
  const completa = esBolillero
    ? elegidos.length === juego.cantidad && (!juego.extra || extra !== null)
    : numero.length === cifras;
  const hayAlgo = esBolillero ? elegidos.length > 0 || extra !== null : numero.length > 0;

  // Qué dice el talón cuando la jugada está a medias. Nunca un número de
  // relleno: el aviso honesto dice exactamente qué falta (N1).
  let falta = '';
  if (!completa) {
    if (esBolillero) {
      const n = juego.cantidad - elegidos.length;
      const pieza = [];
      if (n > 0) pieza.push(`${n} ${n === 1 ? 'número' : 'números'}`);
      if (juego.extra && extra === null) pieza.push(`el ${juego.extra.nombre}`);
      falta = `Te falta elegir ${pieza.join(' y ')}.`;
    } else {
      const n = cifras - numero.length;
      falta = numero.length === 0
        ? 'Escribí tu número, o pedí que te la llenemos.'
        : `Te ${n === 1 ? 'falta' : 'faltan'} ${n} ${n === 1 ? 'cifra' : 'cifras'}.`;
    }
  }

  const resumen = completa
    ? (esBolillero
      ? `Jugada de ${juego.nombre}: ${elegidos.map(pad2).join(', ')}${juego.extra ? `, más el ${juego.extra.nombre} ${extra}` : ''}.`
      : `Jugada de ${juego.nombre}: el ${numero} a la ${posicion === 1 ? 'cabeza' : `posición ${posicion}`}.`)
    : falta;

  /* ───────────────────────── Compartir la jugada ─────────────────────────
     El botón aparece recién con la jugada completa, y no antes en gris: en
     una pieza cuya gracia es armar el cartón, un botón apagado desde el
     primer render es una promesa que no se puede cumplir todavía. Aparece
     como aparecen las modalidades, cuando hay algo de verdad para mostrar.

     Va al pie del talón porque el talón ES la jugada: la imagen que se
     comparte y lo que está mirando la persona son el mismo objeto. */
  const suenoJugada = !esBolillero && numero.length >= 2
    ? suenoDe(numero.slice(-2))
    : null;

  const lineasJugada = esBolillero
    ? [
      elegidos.map(pad2).join(' · '),
      juego.extra && extra !== null ? `+ ${extra} (${juego.extra.corto || juego.extra.nombre})` : null,
    ].filter(Boolean)
    : [`El ${numero} a la ${posicion === 1 ? 'cabeza' : `${posicion}ª posición`} · ${cifras} ${cifras === 1 ? 'cifra' : 'cifras'}`];

  const fraseSueno = suenoJugada
    ? `El ${numero.slice(-2)} es ${suenoJugada} en la tabla de los sueños.`
    : null;

  const hacerImagen = useCallback(() => imagenJugada({
    juego: juego.nombre,
    logo: tema.logo,
    como: juego.comoSeJuega,
    familia: juego.familia,
    color: juego.bola,
    bolillas: elegidos.map(pad2),
    extra: juego.extra && extra !== null
      ? {
        valor: String(extra),
        rotulo: juego.extra.corto || juego.extra.nombre,
        color: juego.extra.bola || juego.bola,
      }
      : null,
    boleta: esBolillero ? null : {
      digitos: numero.split(''),
      posicion: posicion === 1 ? 'cabeza' : `${posicion}ª`,
      cifras: `${cifras} ${cifras === 1 ? 'cifra' : 'cifras'}`,
    },
    sueno: fraseSueno,
  }), [
    juego.nombre, juego.comoSeJuega, juego.familia, juego.bola, juego.extra,
    tema.logo, elegidos, extra, esBolillero, numero, posicion, cifras, fraseSueno,
  ]);

  return (
    <div
      className="sim"
      style={{
        '--sim-bola': juego.bola,
        '--sim-bola-extra': (juego.extra && juego.extra.bola) || juego.bola,
        '--sim-marca': tema.acento,
      }}
    >
      <div className="sim-juegos" role="group" aria-label="Elegí el juego">
        {JUEGOS_SIM.map((j) => {
          const t = temaDe(j.clave, PUB);
          return (
            <button
              key={j.clave}
              type="button"
              className={`sim-juego${j.clave === juego.clave ? ' activo' : ''}`}
              style={{ '--sim-b': t.acento }}
              aria-pressed={j.clave === juego.clave}
              onClick={() => onClave(j.clave)}
            >
              <span className="sim-juego-logo">
                <img src={t.logo} alt="" aria-hidden="true" loading="lazy" />
              </span>
              <span className="sim-juego-nom">{j.nombre}</span>
            </button>
          );
        })}
      </div>

      <div className="sim-carton">
        <header className="sim-carton-head">
          <span className="sim-carton-logo">
            <img src={tema.logo} alt="" aria-hidden="true" />
          </span>
          <span className="sim-carton-tit">
            <span className="sim-carton-rot">Cartón de práctica</span>
            <strong>{juego.nombre}</strong>
          </span>
        </header>

        <p className="sim-como">{juego.comoSeJuega}</p>

        {esBolillero ? (
          <CartonBolillero
            juego={juego}
            elegidos={elegidos}
            extra={extra}
            onToggle={toggle}
            onExtra={(v) => setExtra((p) => (p === v ? null : v))}
          />
        ) : (
          <CartonQuiniela
            juego={juego}
            cifras={cifras}
            numero={numero}
            posicion={posicion}
            onCifras={(c) => { setCifras(c); setNumero((n) => n.slice(-c)); }}
            onNumero={setNumero}
            onPosicion={setPosicion}
            onVerSuenos={onVerSuenos}
          />
        )}

        {/* La jugada aleatoria no es una alternativa al cartón: es el atajo
            de adentro. Y sigue enseñando, porque lo que produce tiene la
            forma correcta de una jugada de este juego. */}
        <div className="sim-acciones">
          <p className="sim-acciones-q">¿No sabés qué elegir?</p>
          <div className="sim-acciones-btns">
            <button type="button" className="sim-btn sim-btn-azar" onClick={llenar}>
              <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true" focusable="false">
                <rect x="3.2" y="3.2" width="17.6" height="17.6" rx="4.4" fill="none" stroke="currentColor" strokeWidth="2" />
                <circle cx="8.4" cy="8.4" r="1.7" fill="currentColor" />
                <circle cx="12" cy="12" r="1.7" fill="currentColor" />
                <circle cx="15.6" cy="15.6" r="1.7" fill="currentColor" />
              </svg>
              Llenámela vos
            </button>
            <button
              type="button"
              className="sim-btn sim-btn-borrar"
              onClick={borrar}
              disabled={!hayAlgo}
            >
              Borrar
            </button>
          </div>
        </div>

        {/* El talón: lo que quedaría impreso si esto fuera un ticket.
            Nunca muestra un número de relleno — si la jugada está a medias,
            dice cuánto falta y deja las bolillas vacías (N1). */}
        <div className="sim-talon" ref={talonRef}>
          <p className="sim-talon-rot">Tu jugada</p>

          {/* La jugada se lee dos veces por caminos distintos: la fila de
              bolillas es puro dibujo (aria-hidden) y la frase de acá abajo es
              la que escucha un lector de pantalla. Así no se dice dos veces
              y la frase queda armada como se habla. */}
          <p className="sim-sr" aria-live="polite">{resumen}</p>

          {esBolillero ? (
            <div className="sim-bolillas" aria-hidden="true">
              <span className="sim-grupo">
                {Array.from({ length: juego.cantidad }, (_, i) => (
                  elegidos[i] === undefined
                    ? <Bolilla key={`v${i}`} vacia />
                    : <Bolilla key={elegidos[i]} valor={pad2(elegidos[i])} />
                ))}
              </span>
              {/* La bola extra va aparte, con otro color y separada por un
                  "+": así se comunica que juega distinto sin una línea de
                  texto. Es el gesto del generador oficial de Loto. */}
              {juego.extra && (
                <span className="sim-extra-wrap">
                  <span className="sim-mas">+</span>
                  <span className="sim-grupo sim-grupo-extra">
                    {extra === null ? <Bolilla vacia /> : <Bolilla valor={extra} extra />}
                    <small>{juego.extra.corto || juego.extra.nombre}</small>
                  </span>
                </span>
              )}
            </div>
          ) : (
            <div className="sim-boleta" aria-hidden="true">
              <span className="sim-boleta-num">
                {Array.from({ length: cifras }, (_, i) => (
                  <span className="sim-digito" key={i}>{numero[i] ?? ''}</span>
                ))}
              </span>
              <span className="sim-boleta-pos">
                a la {posicion === 1 ? <b>cabeza</b> : <b>{posicion}ª</b>}
                <small>{cifras} {cifras === 1 ? 'cifra' : 'cifras'}</small>
              </span>
            </div>
          )}

          <p className="sim-talon-pie">
            {completa ? juego.detalle : falta}
          </p>

          {completa && juego.modalidades && (
            <ul className="sim-modalidades">
              {juego.modalidades.map((m) => <li key={m}>{m}</li>)}
            </ul>
          )}

          {completa && (
            <div className="sim-compartir">
              <BotonCompartir
                hacerArchivo={hacerImagen}
                firma={`${juego.clave}|${lineasJugada.join('|')}`}
                titulo={`Jugada simulada de ${juego.nombre}`}
                texto={textoJugada({
                  juego: juego.nombre,
                  lineas: lineasJugada,
                  sueno: fraseSueno,
                })}
                aria={`Compartir la imagen de esta jugada simulada de ${juego.nombre}. ${resumen} La imagen aclara que es una simulación y no una apuesta.`}
                ariaCopia={`Copiar la imagen de esta jugada simulada de ${juego.nombre}. ${resumen} La imagen aclara que es una simulación y no una apuesta.`}
                variante="noche"
              />
            </div>
          )}
        </div>
      </div>

      <p className="sim-aviso">
        Es un simulador para aprender a jugar: no es una apuesta ni una compra, y
        no se guarda nada. Para jugar de verdad, andá a una agencia oficial.
      </p>
      <p className="sim-fuente">{juego.fuente}</p>
    </div>
  );
}
